(ns app.front.view.registration.authority-user-details
  (:require [app.front.state-machine :as sm]
            [app.front.ui.password :as ui-password]
            [app.front.validate :as validate :refer [field-invalid-text field-invalid?]]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]))


(def error-messages {:user/first-name [[validate/NonBlankString :validate/blank-first-name]]
                     :user/last-name  [[validate/NonBlankString :validate/blank-last-name]]
                     :user/email      [[validate/Email :validate/wrong-email-address]]
                     :user/password   [[validate/NonBlankString :validate/blank-password]]
                     ::password-score [[validate/PasswordScore :validate/password-score]
                                       [validate/PasswordBad :validate/password-bad]]})


(def initial-model {:user/password  ""
                    ::state         nil
                    ::show-errors?  false
                    ::errors        {}
                    ::server-errors nil})


(def state-machine
  {nil                 {::init ::ready}
   ::ready             {::set-form-data ::client-validation
                        ::try-submit    ::submitting}
   ::client-validation {::no-errors    ::ready
                        ::still-errors ::client-validation}
   ::submitting        {::submit-user-exists ::user-exists
                        ::submit-success     ::done}
   ::user-exists       {::set-form-data ::client-validation}})


;; -- Subscriptions -----------------------------------------------------------


(rf/reg-sub
  ::form-data
  (fn [db [_ path]]
    (let [form-data (::form-data db)]
      (if path (get-in form-data path) form-data))))


(rf/reg-sub
  ::state
  :<- [::form-data]
  (fn [form-data _] (get form-data ::state)))


(rf/reg-sub
  ::show-errors?
  :<- [::form-data]
  (fn [form-data _] (get form-data ::show-errors?)))


(rf/reg-sub
  ::server-errors
  :<- [::form-data]
  (fn [form-data _] (get form-data ::server-errors)))


(rf/reg-sub
  ::submit-disabled?
  :<- [::state]
  (fn [state _] (= state ::submitting)))


;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine [::form-data ::state]))

(def debug (rf/after (fn [db event]
                       (.log js/console "=======")
                       (.log js/console "state: " (str (-> db ::form-data ::state)))
                       (.log js/console "event: " (str event)))))


(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])


(defn handle-init
  [db [event user-data]]
  (-> db
      (update ::form-data #(merge % user-data initial-model))
      (update-next-state event)))


(rf/reg-event-fx
  ::set-form-data
  interceptors
  (fn [{:keys [db]} [event path value]]
    {:db       (-> db
                   (assoc-in (into [::form-data] path) value)
                   (update-next-state event))
     :dispatch [::validate-fields]}))


(defn handle-submit-clicked
  [{:keys [db]} _]
  {:db       (assoc-in db [::form-data ::show-errors?] true)
   :dispatch (if-not (empty? (validate/validate-form (::form-data db) error-messages))
               [::validate-fields]
               [::try-submit])})


(defn handle-validate-fields
  [{:keys [db]} _]
  {:dispatch (if (empty? (validate/validate-form (::form-data db) error-messages))
               [::no-errors]
               [::still-errors])})


(rf/reg-event-fx ::try-submit
  (fn [{:keys [db]} [event _]]
    {:db           (update-next-state db event)
     :comm/command {:command        :user/complete-authority-user-profile
                    :data           (-> (::form-data db)
                                        (select-keys [:user/first-name
                                                      :user/last-name
                                                      :user/password
                                                      :user/token])
                                        validate/strip-nil-values)
                    :on-success     [::submit-success]
                    :on-bad-request [::submit-failure]}}))


(rf/reg-event-fx ::submit-success
  (fn [{:keys [db]} [_ _ {:keys [mfa] :as user}]]
    (if-let [mfa-url (:redirect-url mfa)]
      {:navigate-to mfa-url}
      {:db       (assoc db :session/user user)
       :dispatch [::add-notification-go-to-dashboard]})))


(rf/reg-event-fx ::add-notification-go-to-dashboard
  (fn [_ _]
    {:dispatch   [:notifications/add
                  :inline
                  :success
                  :profile/created
                  :cloudpermit/start]
     :route/push :dashboard}))


(rc/reg-chain
  ::submit-failure
  (fn [{:keys [db]} [_ status body]]
    (js/console.error "::submit-failure" status body)
    {:db       db
     :dispatch [::submit-user-exists]}))


(rf/reg-event-db ::init interceptors handle-init)

(rf/reg-event-fx ::validate-fields handle-validate-fields)
(rf/reg-event-fx ::submit-clicked interceptors handle-submit-clicked)

(rf/reg-event-db ::no-errors update-next-state)
(rf/reg-event-db ::still-errors update-next-state)

(rf/reg-event-db ::submit-user-exists interceptors update-next-state)

(defn val->evt [path]
  (fn [e] (>evt [::set-form-data path (-> e .-target .-value)])))


(defn registration-form [tr validator]
  [:form
   {:on-submit   (fn [e] (.preventDefault e) (>evt [::submit-clicked]))
    :no-validate true}
   [:section.flex.flex-col.w-full
    [:fieldset
     [:legend.mb-6.bx--type-heading-03 (tr [:user/first-and-last-name])]
     [:div.flex.mb-6.flex-wrap
      [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
       (cc/text-input {:id            "input-first-name"
                       :label-text    (tr [:user/first-name])
                       :light         true
                       :placeholder   (tr [:placeholder/first-name])
                       :default-value (<sub [::form-data [:user/first-name]])
                       :invalid       (field-invalid? (<sub [::show-errors?]) validator :user/first-name)
                       :invalid-text  (field-invalid-text (<sub [::show-errors?]) validator :user/first-name tr)
                       :on-change     (val->evt [:user/first-name])})]
      [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4  mr-auto"}
       (cc/text-input {:id            "input-last-name"
                       :label-text    (tr [:user/last-name])
                       :light         true
                       :placeholder   (tr [:placeholder/last-name])
                       :default-value (<sub [::form-data [:user/last-name]])
                       :invalid       (field-invalid? (<sub [::show-errors?]) validator :user/last-name)
                       :invalid-text  (field-invalid-text (<sub [::show-errors?]) validator :user/last-name tr)
                       :on-change     (val->evt [:user/last-name])})]]]

    [:fieldset
     [:legend.mb-6.bx--type-heading-03 (tr [:user/email-address])]
     [:div.flex.mb-6.flex-wrap
      [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
       (cc/text-input {:id            "input-email"
                       :label-text    (tr [:user/email-address])
                       :light         true
                       :type          :email
                       :default-value (<sub [::form-data [:user/email]])
                       :disabled      true})]]]

    [:fieldset
     [:legend.mb-6.bx--type-heading-03 (tr [:user/password])]
     [:div.flex
      [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 mr-auto"}
       [ui-password/password-input
        {:tr              tr
         :id              "input-password"
         :label           (tr [:user/password])
         :auto-complete   "new-password"
         :handle-change   (fn [password]
                            (>evt [::set-form-data [:user/password] password]))
         :on-score-change #(>evt [::set-form-data [::password-score] %])
         :use-meter?      true}]]]]

    [:div.flex.flex-col.items-start.mb-6
     [:div.flex.items-center.mb-6
      (cc/button
        {:type             :submit
         :disabled         (<sub [::submit-disabled?])
         :icon             i/check
         :icon-description (tr [:register/activate])}
        (tr [:register/activate]))
      (cc/link {:href (href/href :login) :class-name "ml-10"} (tr [:cancel]))]
     (when (and (<sub [::show-errors?]) (seq (validate/validate-form (<sub [::form-data]) error-messages)))
       [:ul.mb-6
        (for [error (validate/validate-form (<sub [::form-data]) error-messages)]
          [:li.mb-2.text-red
           {:key (str error)}
           [:span.text-danger (tr [error])]])])]]])


;; Registration view:
(defn view [user-data]
  (rf/dispatch-sync [::init user-data])
  (let [tr        (<sub [:tr])
        validator #(or (get (<sub [::server-errors]) %)
                       (validate/validate-field % (<sub [::form-data]) error-messages))]
    (fn []
      [:div.cloudpermit-page
       [:div.page-header
        [:h1.my-8 (tr [:register/heading-activate])]]
       [registration-form tr validator]])))
